import { FC, memo, useEffect, useState, useRef } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { useMediaQuery } from '@mui/material';

import { useRecoilValue } from 'recoil';

import RankIconLeft from '/public/images/iqfu/public/rank_icon_left.svg';
import RankIconRight from '/public/images/iqfu/public/rank_icon_right.svg';

import { useDirectChatPath } from '@hooks_iqfu/public/useDirectChatPath';
import { useInjectLeasebackCaution } from '@hooks_iqfu/public/useInjectLeasebackCaution';
import { useLikedCompanies } from '@hooks_iqfu/public/useLikedCompanies';

import { formatCompanyCardText } from '@utils_iqfu/formatCompanyCardText';

import DisplayThumbImage from '@components_iqfu/public/display_thumb_image/DisplayThumbImage';

import { sellerIsLoggedIn } from '@recoil_iqfu/atoms/public/login_state';
import userUuidValue from '@recoil_iqfu/atoms/public/user_uuid';

import { CompanyListItem } from '@type-def_iqfu/public/CompanyListItem';
import { likedCompanyData } from '@type-def_iqfu/public/likedCompaniesData';

import { MediaQuery } from '@constants_common';
import { saleCaseTypeText } from '@constants_iqfu';
import { CompaniesPageQueryType } from 'src/iqfu/data/companiesPagesData';

import styles from './CompanyCard.module.scss';

type Props = {
  data?: CompanyListItem;
  rank?: number;
  pageQuery: CompaniesPageQueryType | 'simulator' | 'price';
  conversionType?: 'like' | 'direct_chat_link';
  displayCityName?: string;
};

const getOtherData = (data: CompanyListItem) => {
  const access = data.overview_items.filter((item) => item.name === 'アクセス')[0]?.content;
  const holiday = data.overview_items.filter((item) => item.name === '定休日')[0]?.content;
  const businessHours = data.overview_items.filter((item) => item.name === '営業時間')[0]?.content;
  const parking = data.overview_items.filter((item) => item.name === '駐車場')[0]?.content;
  return {
    access,
    holiday,
    businessHours,
    parking,
  };
};

const CompanyCard: FC<Props> = memo(({ data, rank, conversionType = 'direct_chat_link', displayCityName }) => {
  const isMobile = useMediaQuery(MediaQuery.lg);
  const router = useRouter();
  const [isLiked, setIsLiked] = useState(false);
  const userUuid = useRecoilValue(userUuidValue);
  const { dispatchAddLikedCompaniesAction, useGetLikedCompanies } = useLikedCompanies();
  const chatButtonRef = useRef<HTMLButtonElement>(null);

  const isLoggedIn = useRecoilValue(sellerIsLoggedIn);

  const injectLeasebackCautions = useInjectLeasebackCaution();
  const getDirectChatPath = useDirectChatPath();
  const [otherData, setOtherData] = useState(getOtherData(data!));

  const { data: fetchedLikedCompaniesData } = useGetLikedCompanies({
    isLoggedIn,

    userUuid,
  });

  const likedCompanies: likedCompanyData[] = fetchedLikedCompaniesData?.favorite_companies;

  const checkIsLiked = () => {
    return likedCompanies?.filter((company) => company.company_id === data?.id).length > 0;
  };

  const directChatButtonClickHandler = (companyId: number) => {
    const stopProcessing = injectLeasebackCautions();
    if (stopProcessing) return;

    if (!isLiked) {
      dispatchAddLikedCompaniesAction({
        isLoggedIn: isLoggedIn,
        userUuid: userUuid,
        companyId: companyId,
        component: 'CompanyCard',
      });
    }

    router.push(getDirectChatPath());
  };

  useEffect(() => {
    if (setIsLiked) {
      setIsLiked(checkIsLiked());
    }
    //依存にdata入れないとページ遷移時にいいねおかしくなる
  }, [likedCompanies, data]);

  useEffect(() => {
    setOtherData(getOtherData(data!));
  }, [data]);

  //データ読みこみ済み表示

  let companyHasRankClass = '';
  let companyRankClass = '';

  if (rank) {
    companyHasRankClass = styles.hasRank;
    if (rank < 4) {
      companyRankClass = styles[`rank_${rank}`];
    } else {
      companyRankClass = styles.rank_others;
    }
  }

  const pathName = router.pathname;
  const isInCitiesCompaniesPage = pathName.includes('/companies/cities');

  //会社系都道府県の主要以外の検索軸
  //※最後の/ないと主要も含まれてしまう
  const isInPrefecturesSubCompaniesPage = pathName.includes('/companies/prefectures/[prefecture_code]/');

  const isLCPImage = (() => {
    const isFirstRankPanel = rank === 1;

    return (isFirstRankPanel && isInCitiesCompaniesPage) || (isFirstRankPanel && isInPrefecturesSubCompaniesPage);
  })();

  if (!data) return null;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('button') === chatButtonRef.current) {
      e.preventDefault();
      e.stopPropagation();
      directChatButtonClickHandler(data.id);
    }
  };

  return (
    <Link
      className={styles.company_card}
      href={`/companies/${data.id}`}
      target={isMobile ? '_self' : '_blank'}
      onClick={handleCardClick}
    >
      {isMobile && (
        <h3 className={`${styles.company_name_mobile} ${companyHasRankClass} ${companyRankClass}`}>
          {rank && (
            <div className={styles.rankMark}>
              <span className={styles.rankIcon_left}>
                <RankIconLeft />
              </span>
              <span className={styles.rankMark_num}>{rank}</span>位
              <span className={styles.rankIcon_right}>
                <RankIconRight />
              </span>
            </div>
          )}
          {data.display_name}
        </h3>
      )}
      <div className={`${styles.company_left_contents}`}>
        <div className={styles.company_img_wrap}>
          {data.main_image ? (
            <DisplayThumbImage
              originalImagePath={data.main_image}
              thumbSize="medium"
              lazyLoading={!isLCPImage}
              fetchPriority={isLCPImage ? 'high' : 'auto'}
              companyCard={true}
            />
          ) : (
            <div className={styles.no_image}>
              <img
                className={styles.company_img}
                src={'/images/iqfu/public/no_company_image.png'}
                alt="準備中"
                loading="lazy"
                data-gtm-click="DisplayThumbImage_img"
              />
            </div>
          )}
        </div>
        {!isMobile && data.tags && data.tags.length > 0 ? (
          <ul className={styles.tag_container}>
            {data.tags.map((tag, i) => {
              return (
                <li className={styles.tag_item} key={i}>
                  {tag.name}
                </li>
              );
            })}
          </ul>
        ) : (
          <></>
        )}
      </div>
      <div className={styles.info}>
        {!isMobile && (
          <h3 className={`${styles.company_name} ${companyHasRankClass} ${companyRankClass}`}>
            {rank && (
              <div className={styles.rankMark}>
                <span className={styles.rankIcon_left}>
                  <RankIconLeft />
                </span>
                <span className={styles.rankMark_num}>{rank}</span>位
                <span className={styles.rankIcon_right}>
                  <RankIconRight />
                </span>
              </div>
            )}
            {data.display_name}
          </h3>
        )}
        <p className={styles.cassette_title}>{data.cassette_title}</p>

        {isMobile && data.tags && data.tags.length > 0 ? (
          <ul className={styles.tag_container}>
            {data.tags.map((tag, i) => {
              return (
                <li className={styles.tag_item} key={i}>
                  {tag.name}
                </li>
              );
            })}
          </ul>
        ) : (
          <></>
        )}

        {data.within_36_months_sale_case_counts && data.within_36_months_sale_case_counts.count > 0 ? (
          <p className={styles.card_sale_case_count_within_36_months}>
            {isInCitiesCompaniesPage ? `${displayCityName}の` : ''}
            <span className={`${styles.bold} ${styles.lead_text}`}>
              {saleCaseTypeText[data.within_36_months_sale_case_counts.sort_type] ?? '売却実績数'}
            </span>
            <span className={`${styles.orange} ${styles.bold}`}>
              <span className={styles.number}>{data.within_36_months_sale_case_counts.count}</span>件
            </span>
            <span className={styles.annotate}>※3年以内</span>
          </p>
        ) : (
          <></>
        )}
        <table className={styles.other_data_container}>
          <tbody>
            <tr className={styles.other_data}>
              <th className={styles.other_data_title}>所在地</th>
              <td className={styles.other_data_content}>{data.full_address || '-'}</td>
            </tr>

            <tr className={styles.other_data}>
              <th className={styles.other_data_title}>アクセス</th>
              <td className={styles.other_data_content}>{formatCompanyCardText(otherData.access, 36) || '-'}</td>
            </tr>

            {!isMobile && otherData.businessHours && (
              <tr className={styles.other_data}>
                <th className={styles.other_data_title}>営業時間</th>
                <td className={styles.other_data_content}>
                  {formatCompanyCardText(otherData.businessHours, 36) || '-'}
                </td>
              </tr>
            )}
            {!isMobile && otherData.holiday && (
              <tr className={styles.other_data}>
                <th className={styles.other_data_title}>定休日</th>
                <td className={styles.other_data_content}>{formatCompanyCardText(otherData.holiday, 36) || '-'}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className={styles.button_container}>
          <div className={styles.button_stacks}>
            <div className={styles.detail_button}>詳しくみる</div>
            {conversionType === 'direct_chat_link' && (
              <button ref={chatButtonRef} className={styles.chat_button}>
                <span className={styles.chat_button_small}>無料</span>
                <span>売却相談する</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
});

export default CompanyCard;

CompanyCard.displayName = 'CompanyCard';
